import { useState, useCallback } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { useTheme } from "./theme";
import {
  FadeIn, withAlpha,
} from "./primitives";
import { font, radius, shadow } from "./tokens";
import { findIngredient } from "../../data/ingredients";
import { brandsForCanonical } from "../../data/brandExpertise";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { useShoppingRegion } from "../../lib/useShoppingRegion";

// Aggregate retailer hints from a canonical's brand records — drives
// the "Find at …" line on each shopping row. Pulls `stores` arrays
// from every brand `brandsForCanonical` returns (curated + USDA-derived
// + Tier-5 AI-populated regional brands), unions them, caps at 4 names.
// `exclusive_to` short-circuits with a stronger statement.
function storeHintForCanonical(ingredientId, info, region) {
  if (!ingredientId) return null;
  let brands;
  try {
    brands = brandsForCanonical(ingredientId, { info, region, limit: 12 });
  } catch {
    return null;
  }
  if (!Array.isArray(brands) || brands.length === 0) return null;

  const exclusive = brands.find((b) => b && typeof b.exclusive_to === "string" && b.exclusive_to.trim());
  if (exclusive) return `Only at ${exclusive.exclusive_to.trim()}`;

  const seen = new Set();
  const ordered = [];
  for (const b of brands) {
    if (!Array.isArray(b?.stores)) continue;
    for (const s of b.stores) {
      const name = typeof s === "string" ? s.trim() : "";
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      ordered.push(name);
    }
  }
  if (ordered.length === 0) return null;
  const head = ordered.slice(0, 4).join(", ");
  return ordered.length > 4 ? `Find at ${head} +${ordered.length - 4}` : `Find at ${head}`;
}

// Source-badge label. Short uppercase string in DM Mono — shopping
// items can come from manual entry, low-stock alerts, or recipe-add
// flows. Hidden for the "manual" case since that's the default and
// reads as noise on every row.
function badgeForSource(src) {
  if (src === "low-stock") return "LOW STOCK";
  if (src === "recipe")    return "FROM RECIPE";
  return null;
}

// Shopping aisle taxonomy. Ordered by typical grocery-store flow
// (perimeter first — produce, bakery, meat, dairy — then interior
// dry/frozen, then beverages on the way out). The order is the
// rendered order on screen so the list reads as a route through
// the store. "Other" is the fallback for items the classifier
// can't place — kept last so it doesn't clutter the top.
const AISLES = [
  { id: "produce",   label: "Produce" },
  { id: "bakery",    label: "Bakery" },
  { id: "meat",      label: "Meat & Seafood" },
  { id: "dairy",     label: "Dairy & Eggs" },
  { id: "frozen",    label: "Frozen" },
  { id: "pantry",    label: "Pantry" },
  { id: "beverages", label: "Beverages" },
  { id: "other",     label: "Other" },
];

// Map a shopping-list item to an aisle bucket. Reads item.category
// first (the field set by QuickAdd / scan flows / canonical
// resolution), falls back to the canonical's own category when the
// item carries an ingredientId. Items with neither end up in
// "other" — shouldn't happen often but the fallback keeps the
// classifier total.
function aisleFor(item) {
  const cat = String(item?.category || "").toLowerCase().trim();
  if (cat === "produce" || cat === "fruit" || cat === "vegetable") return "produce";
  if (cat === "meat" || cat === "seafood")    return "meat";
  if (cat === "dairy")                        return "dairy";
  if (cat === "bread" || cat === "bakery")    return "bakery";
  if (cat.startsWith("frozen"))               return "frozen";
  if (cat === "drinks" || cat === "beverage") return "beverages";
  if (cat === "pantry" || cat === "condiments" || cat === "herbs" || cat === "leftovers") return "pantry";
  // Canonical fallback — surfaces "produce" for fresh herbs,
  // "meat" for chicken/beef/etc. when the item was added via a
  // canonical pick but never carried a category.
  if (item?.ingredientId) {
    const ing = findIngredient(item.ingredientId);
    if (ing?.category) return aisleFor({ category: ing.category });
  }
  return "other";
}

// Group items by aisle into ordered, non-empty sections so the
// renderer can iterate without checking emptiness inline.
function groupByAisle(items) {
  const byId = new Map(AISLES.map((a) => [a.id, []]));
  for (const item of items) {
    const aisleId = aisleFor(item);
    (byId.get(aisleId) || byId.get("other")).push(item);
  }
  return AISLES
    .map((a) => ({ ...a, items: byId.get(a.id) || [] }))
    .filter((a) => a.items.length > 0);
}

export function MCMShoppingScreen({
  shoppingList = [],
  // spendCents + onOpenReceipts intentionally NOT surfaced on this
  // screen. Receipt totals belong on the front page; this screen is
  // strictly about the upcoming trip. Props stay in the signature
  // so the App.jsx call site doesn't have to change — they're just
  // unused here on purpose.
  // eslint-disable-next-line no-unused-vars
  spendCents,
  // eslint-disable-next-line no-unused-vars
  onOpenReceipts,
  // viewerId / nameFor / avatarFor — drive the per-row "added by"
  // pip. App.jsx provides nameFor + avatarFor closures keyed on
  // user_id (already used by the activity feed). viewerId hides
  // the pip on the user's own items so it only surfaces family
  // members' contributions.
  viewerId,
  nameFor,
  avatarFor,
  onBackToKitchen,
  onAdd,
  // Rapid-fire text jotting (ShoppingQuickAdd) — sibling of the
  // wizard-backed onAdd. Optional; the JOT button hides without it.
  onQuickAdd,
  onShopMode,
  onCheckOff,
  onRemove,
}) {
  const { theme } = useTheme();
  // getInfo: merged ingredient_info row for a canonical (already in
  // memory via useIngredientInfo's reactive map — no extra egress).
  // Drives the "Find at …" line on each row.
  const { getInfo } = useIngredientInfo();
  const [region] = useShoppingRegion();

  // Local "got it" state — items the user has checked off but not
  // yet committed. Stored as a Set of item.id values; the actual
  // shopping list in App.jsx is unchanged until the user picks an
  // action (Send to Kitchen / Remove). This lets users mark items
  // as picked-up while in the store, then decide en bulk whether
  // to mint kitchen rows OR just clear the list (Shop Mode-paired
  // items don't need to be re-added; manual jots might).
  const [checkedIds, setCheckedIds] = useState(() => new Set());

  const toggleChecked = useCallback((id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Filter out checked-off rows (Shop Mode flips purchasedAt on
  // commit) so the list only shows items still to buy. Reverse-sort
  // so the most recently added entry shows up first — kept simple
  // since shopping items don't carry a clear ordering field.
  const toBuy = (Array.isArray(shoppingList) ? shoppingList : [])
    .filter((it) => !it?.purchasedAt);
  // Split into unchecked (still in aisle sections) and checked
  // (in the "Got it" section at the bottom). Both keep their item
  // shape; only the rendering differs.
  const unchecked = toBuy.filter((it) => !checkedIds.has(it.id));
  const checked   = toBuy.filter((it) =>  checkedIds.has(it.id));
  const totalCount = toBuy.length;
  const checkedCount = checked.length;

  // Bulk actions on the "Got it" set. The auto-commit "Send to
  // Kitchen" path is intentionally absent: typed shopping-list
  // text is too fragile to mint accurate kitchen rows from
  // (no canonical confidence, no package size, no brand, no shelf
  // life). The honest path is to route through Shop Mode's
  // scan-pair flow — UPC scans + receipt OCR pair the user's
  // physical items to the list entries with high data fidelity.
  // The user picks: pair via scan (full data, commits real kitchen
  // rows), or remove (the items don't need a kitchen entry — common
  // for refills the user wants tracked but not stocked).
  const pairCheckedViaScan = useCallback(() => {
    if (!onShopMode) return;
    onShopMode();
    // Note: we do NOT clear checkedIds here. The user's local check
    // state is preserved so that when they return from Shop Mode,
    // they can see what they had marked as "got it" — and the
    // committed-via-Shop-Mode items have already been removed from
    // shoppingList by Shop Mode's checkout, so they fall out of the
    // checked set automatically on next render.
  }, [onShopMode]);

  const removeChecked = useCallback(() => {
    for (const item of checked) {
      onRemove && onRemove(item.id);
    }
    setCheckedIds(new Set());
  }, [checked, onRemove]);

  return (
    <div
      style={{
        minHeight: "100dvh",
        color: theme.color.ink,
        // Mirror KitchenScreen's outer padding so the hero clears the
        // iOS notch and the content reserves runway above the bottom
        // nav + floating dock.
        padding: "calc(28px + env(safe-area-inset-top)) 20px 180px",
        position: "relative",
      }}
    >
      {/* Top-right back affordance — DM Mono uppercase chip styled
          to match the kitchen's BackChip language. Sits absolutely
          so the hero column remains visually anchored to the left
          edge. */}
      {onBackToKitchen && (
        <button
          type="button"
          onClick={onBackToKitchen}
          style={{
            position: "absolute",
            top: "calc(22px + env(safe-area-inset-top))",
            right: 20,
            zIndex: 4,
            background: "transparent",
            border: `1px solid ${theme.color.hairline}`,
            color: theme.color.skyInkMuted,
            borderRadius: radius.pill,
            padding: "8px 14px",
            fontFamily: font.mono,
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Kitchen
        </button>
      )}

      {/* Hero — Pale Martini display title, DM Mono kicker, soft
          editorial subtitle. Same staggered FadeIn cadence as
          KitchenScreen so the surface reads as part of the same
          family. */}
      <FadeIn>
        <div
          style={{
            fontFamily: font.mono,
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: theme.color.skyInkMuted,
          }}
        >
          Shopping
        </div>
      </FadeIn>

      <FadeIn delay={0.07}>
        <h1
          style={{
            margin: "4px 0 0 0",
            color: theme.color.skyInk,
            fontFamily: font.display,
            fontStyle: "normal",
            fontWeight: 500,
            letterSpacing: "0.035em",
            fontSize: "clamp(40px, 7vw, 64px)",
            lineHeight: 1.0,
          }}
        >
          The List
        </h1>
      </FadeIn>

      <FadeIn delay={0.14}>
        <p
          style={{
            marginTop: 10,
            fontFamily: font.detail,
            fontStyle: "italic",
            fontSize: 15,
            color: theme.color.skyInkMuted,
            lineHeight: 1.45,
            maxWidth: 360,
          }}
        >
          {totalCount === 0
            ? "Nothing on the list yet — tap Add to start a trip."
            : totalCount === 1
              ? "One thing left to grab."
              : `${totalCount} things still to grab.`}
        </p>
      </FadeIn>

      {/* Shop Mode — full-bleed image banner. Uses shopping-mode.png
          as the background art; copy overlays bottom-left with a
          subtle gradient for legibility across the artwork. */}
      {onShopMode && (
        <button
          type="button"
          onClick={onShopMode}
          aria-label="Shop Mode — scan as you shop, auto-pair to the list"
          style={{
            display: "block",
            width: "100%",
            marginTop: 26,
            padding: 0,
            border: `1px solid ${theme.color.hairline}`,
            borderRadius: radius.card,
            boxShadow: shadow.soft,
            overflow: "hidden",
            position: "relative",
            cursor: "pointer",
            backgroundColor: theme.color.glassFillHeavy,
            backgroundImage: "url(/img/shopping-mode.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            aspectRatio: "16 / 7",
            minHeight: 160,
            textAlign: "left",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 18,
              right: 18,
              bottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 0,
            }}
          >
            <div
              style={{
                fontFamily: font.itemTitle,
                fontSize: 22,
                fontWeight: 500,
                fontStyle: "italic",
                lineHeight: 1.05,
                color: "#fff",
                textShadow: "0 2px 8px rgba(0,0,0,0.45)",
              }}
            >
              Shop Mode
            </div>
            <div
              style={{
                fontFamily: font.sans,
                fontSize: 13,
                lineHeight: 1.3,
                color: "rgba(255,255,255,0.92)",
                textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                marginTop: 2,
              }}
            >
              Scan as you shop, auto-pair to the list.
            </div>
          </div>
        </button>
      )}

      {/* List section */}
      <section style={{ marginTop: 32 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
            gap: 12,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 8,
              fontFamily: font.mono,
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: theme.color.inkMuted,
            }}
          >
            <span>Your list</span>
            {totalCount > 0 && (
              <span style={{ color: theme.color.inkFaint }}>{totalCount}</span>
            )}
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {onQuickAdd && (
            <button
              type="button"
              onClick={onQuickAdd}
              aria-label="Quick add — type a few items"
              style={{
                // Text sibling of the + button: rapid-fire jotting
                // ("capers, saffron") without the full wizard. Took
                // over ShoppingQuickAdd's old CreateMenu hero-card
                // slot when the two add cards collapsed into one.
                height: 32,
                padding: "0 12px",
                borderRadius: 16,
                border: `1px solid ${withAlpha(theme.color.teal, 0.35)}`,
                background: withAlpha(theme.color.teal, 0.12),
                color: theme.color.teal,
                fontFamily: font.mono,
                fontSize: 12,
                letterSpacing: "0.08em",
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              JOT
            </button>
          )}
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              aria-label="Add to list"
              style={{
                // Teal-accent treatment — matches the kitchen
                // search-bar's `+` button so the two add affordances
                // read as the same affordance language. Teal tint at
                // 0.12 alpha gives a clear pop on every theme
                // (cream-like surfaces visibly tint, dark night bg
                // gets a teal halo).
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: `1px solid ${withAlpha(theme.color.teal, 0.35)}`,
                background: withAlpha(theme.color.teal, 0.12),
                color: theme.color.teal,
                fontFamily: font.mono,
                fontSize: 20,
                fontWeight: 300,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              +
            </button>
          )}
          </div>
        </div>

        {totalCount === 0 ? (
          <EmptyState theme={theme} onAdd={onAdd} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {groupByAisle(unchecked).map((aisle) => (
              <AisleSection
                key={aisle.id}
                theme={theme}
                aisle={aisle}
                viewerId={viewerId}
                nameFor={nameFor}
                avatarFor={avatarFor}
                getInfo={getInfo}
                region={region}
                checked={false}
                onToggleChecked={toggleChecked}
                onRemove={onRemove}
              />
            ))}
            {checkedCount > 0 && (
              <GotItSection
                theme={theme}
                items={checked}
                viewerId={viewerId}
                nameFor={nameFor}
                avatarFor={avatarFor}
                getInfo={getInfo}
                region={region}
                onToggleChecked={toggleChecked}
                onRemove={onRemove}
              />
            )}
            <p
              style={{
                marginTop: 0,
                fontFamily: font.mono,
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: theme.color.inkFaint,
                textAlign: "center",
              }}
            >
              Swipe right to check off · swipe left to remove
            </p>
          </div>
        )}
      </section>

      {/* Sticky bottom action bar — only renders when there's at
          least one checked item. Two CTAs: Send to Kitchen (mints
          kitchen rows + clears checked) or Remove (deletes checked
          from the list). The user is never auto-committed; some
          items get scanned + paired in Shop Mode and shouldn't
          double-up as kitchen rows. */}
      <AnimatePresence>
        {checkedCount > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: "calc(96px + env(safe-area-inset-bottom))",
              zIndex: 50,
              display: "flex",
              justifyContent: "center",
              padding: "0 16px",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                pointerEvents: "auto",
                width: "100%",
                maxWidth: 480,
                display: "flex",
                gap: 10,
                padding: 10,
                background: theme.color.glassFillHeavy,
                border: `1px solid ${theme.color.hairline}`,
                borderRadius: radius.pill,
                boxShadow: theme.shadow.lift,
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
              }}
            >
              <button
                type="button"
                onClick={removeChecked}
                style={{
                  flex: 1,
                  padding: "12px 14px",
                  background: "transparent",
                  border: `1px solid ${theme.color.hairline}`,
                  color: theme.color.inkMuted,
                  borderRadius: radius.pill,
                  fontFamily: font.mono,
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Remove · {checkedCount}
              </button>
              <button
                type="button"
                onClick={pairCheckedViaScan}
                disabled={!onShopMode}
                style={{
                  flex: 2,
                  padding: "12px 18px",
                  background: theme.color.teal,
                  border: "none",
                  color: "#FFFCF4",
                  borderRadius: radius.pill,
                  fontFamily: font.mono,
                  fontSize: 12,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  cursor: onShopMode ? "pointer" : "not-allowed",
                  opacity: onShopMode ? 1 : 0.5,
                }}
              >
                Pair via scan · {checkedCount}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Stub-style section header at the bottom of the list — gathers
// every locally-checked item under one "Got it · N" kicker. Items
// land here when the user taps the check circle or swipes right.
// They DON'T leave the shopping_list state until the user picks
// a bulk action (Send to kitchen / Remove) on the floating CTA bar.
function GotItSection({ theme, items, viewerId, nameFor, avatarFor, getInfo, region, onToggleChecked, onRemove }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: 8,
          paddingLeft: 4,
          fontFamily: font.mono,
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: theme.color.teal,
        }}
      >
        <span>Got it</span>
        <span style={{ color: withAlpha(theme.color.teal, 0.6) }}>{items.length}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <ShoppingRow
              key={item.id}
              theme={theme}
              item={item}
              viewerId={viewerId}
              nameFor={nameFor}
              avatarFor={avatarFor}
              getInfo={getInfo}
              region={region}
              checked={true}
              onToggleChecked={onToggleChecked}
              onRemove={onRemove}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function AisleSection({ theme, aisle, viewerId, nameFor, avatarFor, getInfo, region, checked, onToggleChecked, onRemove }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: 8,
          paddingLeft: 4,
          fontFamily: font.mono,
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: theme.color.inkMuted,
        }}
      >
        <span>{aisle.label}</span>
        <span style={{ color: theme.color.inkFaint }}>{aisle.items.length}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <AnimatePresence initial={false}>
          {aisle.items.map((item) => (
            <ShoppingRow
              key={item.id}
              theme={theme}
              item={item}
              viewerId={viewerId}
              nameFor={nameFor}
              avatarFor={avatarFor}
              getInfo={getInfo}
              region={region}
              checked={checked}
              onToggleChecked={onToggleChecked}
              onRemove={onRemove}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function CTACard({ theme, tone, kicker, title, subtitle, onClick }) {
  const accent = tone === "teal" ? theme.color.teal : theme.color.burnt;
  const tint   = tone === "teal" ? theme.color.tealTint : theme.color.burntTint;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      style={{
        // Match the shopping-row tile treatment exactly. Uses
        // theme.color.glassFillHeavy (per-theme bright card token)
        // rather than theme.color.paper because paper is dark on
        // night/dawn/dusk themes and would render the card dark.
        textAlign: "left",
        background: theme.color.glassFillHeavy,
        border: `1px solid ${theme.color.hairline}`,
        borderRadius: radius.card,
        boxShadow: shadow.soft,
        padding: 16,
        cursor: "pointer",
        minHeight: 110,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        position: "relative",
        overflow: "hidden",
      }}
    >
        {/* Soft tinted blob in the corner so the CTA reads as
            axis-colored without the entire surface tinting. */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: -24,
            right: -24,
            width: 90,
            height: 90,
            borderRadius: "50%",
            background: tint,
            filter: "blur(10px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            fontFamily: font.mono,
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: accent,
            position: "relative",
          }}
        >
          {kicker}
        </div>
        <div
          style={{
            fontFamily: font.itemTitle,
            fontSize: 16, fontWeight: 500, fontStyle: "italic",
            lineHeight: 1.15,
            color: theme.color.ink,
            position: "relative",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: font.detail,
            fontStyle: "italic",
            fontSize: 13,
            lineHeight: 1.35,
            color: theme.color.inkMuted,
            position: "relative",
          }}
        >
          {subtitle}
        </div>
    </motion.button>
  );
}

// Swipe threshold — past this distance in either direction, releasing
// fires the gesture's action (check off / remove). Lower than the
// drag-constraint extents so users have room to tug the row past the
// commit zone before letting go.
const SWIPE_THRESHOLD = 90;

function ShoppingRow({ theme, item, viewerId, nameFor, avatarFor, getInfo, region, checked = false, onToggleChecked, onRemove }) {
  const badge = badgeForSource(item?.source);
  const qty = formatQty(item);
  // "Find at …" hint — pulls retailer names from the canonical's
  // brand records (curated + USDA-derived + AI regional). Null when
  // we don't have a canonical or none of its brands carry stores
  // metadata; the line is hidden in that case.
  const storeHint = item?.ingredientId
    ? storeHintForCanonical(
        item.ingredientId,
        typeof getInfo === "function" ? getInfo(item.ingredientId) : null,
        region,
      )
    : null;

  // "Added by" pip — always renders when we know who added the
  // item, including the viewer themselves. Resolves through the
  // same closures the activity feed uses, so name + avatar stay in
  // sync without an extra fetch. Falls back to a colored initial
  // when the user has no avatar_url.
  const ownerId = item?.ownerId || null;
  const ownerName = ownerId && typeof nameFor === "function"
    ? nameFor(ownerId) : null;
  const ownerAvatar = ownerId && typeof avatarFor === "function"
    ? avatarFor(ownerId) : null;
  const ownerInitial = (ownerName || "?")[0]?.toUpperCase() || "?";

  // x — horizontal drag offset of the row above the action layers.
  // Action layers underneath fade + scale based on |x| so the user
  // sees their gesture progressing in real time. Past the threshold
  // (cued by stronger color), releasing fires the action.
  const x = useMotionValue(0);
  // Right-swipe (positive x) reveals the CHECK action under the row.
  // Left-swipe (negative x) reveals the REMOVE action.
  const checkOpacity  = useTransform(x, [0, 30, SWIPE_THRESHOLD], [0, 0.5, 1]);
  const removeOpacity = useTransform(x, [-SWIPE_THRESHOLD, -30, 0], [1, 0.5, 0]);
  // Background-color "pre-arm" tint on the row itself so the user
  // can feel they've crossed the firing line before they let go.
  const rowBg = useTransform(
    x,
    [-SWIPE_THRESHOLD, -10, 0, 10, SWIPE_THRESHOLD],
    [
      withAlpha(theme.color.burnt, 0.12),
      theme.color.glassFillHeavy,
      theme.color.glassFillHeavy,
      theme.color.glassFillHeavy,
      withAlpha(theme.color.teal, 0.12),
    ],
  );

  function handleDragEnd(_, info) {
    if (info.offset.x > SWIPE_THRESHOLD) {
      // Right-swipe = toggle check. Doesn't commit; the row moves
      // to the "Got it" section and the user picks Send/Remove on
      // the floating CTA bar.
      onToggleChecked && onToggleChecked(item.id);
    } else if (info.offset.x < -SWIPE_THRESHOLD) {
      onRemove && onRemove(item.id);
    }
    // If neither threshold crossed, framer's spring returns x to 0
    // automatically since the row's natural position is 0.
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4, scale: 0.98 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      style={{ position: "relative" }}
    >
      {/* Action layers behind the row. Two abs-positioned bars that
          fade in under the row as it slides left/right. The check
          layer (teal) lives on the left because right-swipe REVEALS
          the left side; the remove layer (burnt) lives on the right
          because left-swipe reveals the right side. */}
      <motion.div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: radius.card,
          background: withAlpha(theme.color.teal, 0.85),
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingLeft: 22,
          color: "#fff",
          fontFamily: font.mono,
          fontSize: 11,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          gap: 8,
          opacity: checkOpacity,
          pointerEvents: "none",
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>✓</span>
        <span>Got it</span>
      </motion.div>
      <motion.div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: radius.card,
          background: withAlpha(theme.color.burnt, 0.85),
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          paddingRight: 22,
          color: "#fff",
          fontFamily: font.mono,
          fontSize: 11,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          gap: 8,
          opacity: removeOpacity,
          pointerEvents: "none",
        }}
      >
        <span>Remove</span>
        <span style={{ fontSize: 18, lineHeight: 1 }}>✕</span>
      </motion.div>

      {/* The draggable row itself. drag="x" only — vertical drag would
          fight the page scroll. dragElastic 0.15 dampens motion past
          the constraints so a hard fling feels firm not floppy.
          dragSnapToOrigin returns to x:0 on release when no threshold
          crossed; the actual fire-action logic runs in onDragEnd. */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -160, right: 160 }}
        dragElastic={0.15}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        style={{
          x,
          touchAction: "pan-y",
        }}
      >
      <motion.div
        animate={{ opacity: checked ? 0.6 : 1 }}
        transition={{ duration: 0.2 }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: 14,
          background: rowBg,
          border: `1px solid ${theme.color.hairline}`,
          boxShadow: shadow.soft,
          borderRadius: radius.card,
        }}
      >
        <button
          type="button"
          onClick={() => onToggleChecked && onToggleChecked(item.id)}
          aria-label={checked ? `Uncheck ${item.name}` : `Check off ${item.name}`}
          aria-pressed={checked}
          style={{
            background: "transparent",
            border: "none",
            padding: 4,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {/* Animated check circle. Empty ring when unchecked,
              fills with teal + scales-in the ✓ glyph when checked.
              Tapping toggles state — gives forgiveness if you
              checked the wrong item. */}
          <motion.span
            animate={{
              backgroundColor: checked ? theme.color.teal : "rgba(0,0,0,0)",
              borderColor: checked ? theme.color.teal : withAlpha(theme.color.teal, 0.45),
              scale: checked ? [1, 1.18, 1] : 1,
            }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              borderWidth: 1.5,
              borderStyle: "solid",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AnimatePresence>
              {checked && (
                <motion.span
                  key="check"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    color: "#FFFCF4",
                    fontSize: 14,
                    lineHeight: 1,
                    fontWeight: 700,
                  }}
                >
                  ✓
                </motion.span>
              )}
            </AnimatePresence>
          </motion.span>
        </button>

        {item.emoji ? (
          <span
            aria-hidden
            style={{
              fontSize: 22,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {item.emoji}
          </span>
        ) : null}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: font.itemTitle,
              fontSize: 16, fontWeight: 500, fontStyle: "italic",
              lineHeight: 1.15,
              color: theme.color.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.name || "Untitled"}
          </div>
          {(qty || badge) && (
            <div
              style={{
                marginTop: 2,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontFamily: font.mono,
                fontSize: 10,
                letterSpacing: "0.12em",
                color: theme.color.inkMuted,
                textTransform: "uppercase",
              }}
            >
              {qty && <span>{qty}</span>}
              {qty && badge && (
                <span style={{ color: theme.color.inkFaint }}>·</span>
              )}
              {badge && (
                <span style={{ color: theme.color.burnt }}>{badge}</span>
              )}
            </div>
          )}
          {storeHint && (
            <div
              style={{
                marginTop: 3,
                fontFamily: font.detail,
                fontStyle: "italic",
                fontSize: 12,
                lineHeight: 1.3,
                color: theme.color.inkMuted,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {storeHint}
            </div>
          )}
        </div>

        {/* Added-by avatar — only shown for items the viewer didn't
            add themselves. Placed before the ✕ remove button so it
            sits at the row's right edge, immediately readable as
            "this came from a family member." No tooltip (doesn't
            work on mobile) and no name text — the avatar IS the
            identifier. */}
        {ownerId && (
          <span
            aria-label={ownerName ? `Added by ${ownerName}` : "Added"}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              overflow: "hidden",
              background: ownerAvatar
                ? "transparent"
                : withAlpha(theme.color.teal, 0.45),
              color: theme.color.paper,
              fontFamily: font.mono,
              fontSize: 10,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              flexShrink: 0,
              border: `1px solid ${theme.color.hairline}`,
            }}
          >
            {ownerAvatar ? (
              <img
                src={ownerAvatar}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                draggable={false}
              />
            ) : (
              ownerInitial
            )}
          </span>
        )}

        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label={`Remove ${item.name}`}
            style={{
              background: "transparent",
              border: "none",
              padding: 8,
              cursor: "pointer",
              fontFamily: font.mono,
              fontSize: 16,
              color: theme.color.inkFaint,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        )}
      </motion.div>
      </motion.div>
    </motion.div>
  );
}

function EmptyState({ theme, onAdd }) {
  return (
    <div
      style={{
        marginTop: 6,
        padding: 22,
        borderRadius: radius.card,
        border: `1px dashed ${theme.color.hairline}`,
        background: theme.color.glassFillHeavy,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 16,
          color: theme.color.inkMuted,
          lineHeight: 1.4,
        }}
      >
        The list is empty. Add what you need before the next trip.
      </div>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          style={{
            marginTop: 14,
            background: theme.color.burnt,
            color: theme.color.paper,
            border: "none",
            borderRadius: radius.pill,
            padding: "10px 20px",
            fontFamily: font.mono,
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Add the first thing
        </button>
      )}
    </div>
  );
}

// Format the qty line — "3 oz", "×2", "1 lb", or empty.
function formatQty(item) {
  const amt = Number(item?.amount);
  const unit = (item?.unit || "").trim();
  if (!Number.isFinite(amt) || amt <= 0) return null;
  if (!unit) {
    // Bare quantity — show as "×N" only when N > 1, otherwise hide
    // (a single item without a unit reads as redundant noise).
    return amt > 1 ? `×${amt}` : null;
  }
  return `${amt} ${unit}`;
}

export default MCMShoppingScreen;
